export type DocumentReferenceOrdering = 'filename' | '-filename' | 'uploaded_date' | '-uploaded_date';

export type DocumentReferenceSortInput = {
  document_reference_id: number;
  file: {
    filename?: string | null;
    file_type?: string | null;
    uploaded_date?: Date | string | null;
  };
  full_filename?: string | null;
};

export const getDocumentReferenceFileName = (documentReference: DocumentReferenceSortInput) => {
  if (documentReference.full_filename) {
    return documentReference.full_filename;
  }

  const { file_type: fileType } = documentReference.file;
  const filename = documentReference.file.filename ?? '';

  if (!filename) {
    return fileType ?? '';
  }

  return fileType ? `${filename}.${fileType}` : filename;
};

export const getNormalizedDocumentReferenceOrdering = (
  ordering?: string | null
): DocumentReferenceOrdering => {
  if (
    ordering === 'filename'
    || ordering === '-filename'
    || ordering === 'uploaded_date'
    || ordering === '-uploaded_date'
  ) {
    return ordering;
  }

  return 'filename';
};

const compareDocumentReferencesByName = (
  documentReferenceA: DocumentReferenceSortInput,
  documentReferenceB: DocumentReferenceSortInput
) => {
  const fileNameComparison = getDocumentReferenceFileName(documentReferenceA).localeCompare(
    getDocumentReferenceFileName(documentReferenceB),
    undefined,
    { sensitivity: 'base' }
  );

  return fileNameComparison || documentReferenceA.document_reference_id - documentReferenceB.document_reference_id;
};

const getDocumentReferenceUploadedTimestamp = (documentReference: DocumentReferenceSortInput) => {
  const uploadedDate = documentReference.file.uploaded_date;

  if (!uploadedDate) {
    return null;
  }

  const timestamp = uploadedDate instanceof Date ? uploadedDate.getTime() : Date.parse(uploadedDate);

  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareDocumentReferencesByUploadedDate = (
  documentReferenceA: DocumentReferenceSortInput,
  documentReferenceB: DocumentReferenceSortInput,
  isDescending: boolean
) => {
  const uploadedTimestampA = getDocumentReferenceUploadedTimestamp(documentReferenceA);
  const uploadedTimestampB = getDocumentReferenceUploadedTimestamp(documentReferenceB);

  if (uploadedTimestampA === null && uploadedTimestampB === null) {
    return compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
  }

  if (uploadedTimestampA === null) {
    return 1;
  }

  if (uploadedTimestampB === null) {
    return -1;
  }

  const timestampComparison = uploadedTimestampA - uploadedTimestampB;
  const orderedTimestampComparison = isDescending ? -timestampComparison : timestampComparison;

  return orderedTimestampComparison || compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
};

export const getDocumentReferenceComparator = <TDocumentReference extends DocumentReferenceSortInput>(
  ordering?: string | null
) => {
  const normalizedOrdering = getNormalizedDocumentReferenceOrdering(ordering);

  if (normalizedOrdering === '-filename') {
    return (
      documentReferenceA: TDocumentReference,
      documentReferenceB: TDocumentReference
    ) => -compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
  }

  if (normalizedOrdering === 'uploaded_date' || normalizedOrdering === '-uploaded_date') {
    return (
      documentReferenceA: TDocumentReference,
      documentReferenceB: TDocumentReference
    ) => compareDocumentReferencesByUploadedDate(
      documentReferenceA,
      documentReferenceB,
      normalizedOrdering === '-uploaded_date'
    );
  }

  return (
    documentReferenceA: TDocumentReference,
    documentReferenceB: TDocumentReference
  ) => compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
};
